import { useEffect, useState, useCallback, useMemo } from "react";
import api, { apiErr } from "@/lib/api";
import { hhmm } from "@/lib/format";
import { PageHeader, Empty } from "@/components/Bits";
import { JobTitle } from "@/components/JobTitle";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

const STATUS = { awaiting_work: "Awaiting", in_progress: "In progress", blocked: "Blocked", ready_for_inspection: "Inspection", ready_for_delivery: "Delivery", completed: "Done" };
const uniq = (arr) => [...new Map(arr.filter((x) => x.id).map((x) => [x.id, x])).values()].sort((a, b) => a.name.localeCompare(b.name));

function Filter({ label, value, onChange, opts, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full sm:w-48" data-testid={testid}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent><SelectItem value="all">All {label.toLowerCase()}s</SelectItem>{opts.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function LiveJobs() {
  const [rows, setRows] = useState(null);
  const [f, setF] = useState({ emp: "all", task: "all", cust: "all", job: "all" });
  const [err, setErr] = useState("");
  const load = useCallback(() => api.get("/live-jobs").then((r) => { setRows(r.data); setErr(""); }).catch((e) => { setErr(apiErr(e)); setRows((s) => s || []); }), []);
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);
  const o = useMemo(() => {
    const r = rows || [];
    return {
      emp: uniq(r.flatMap((x) => [...x.assigned, ...x.working_now, ...x.booked_by])),
      task: uniq(r.map((x) => ({ id: x.task, name: x.task }))),
      cust: uniq(r.map((x) => ({ id: x.customer_id, name: x.customer_name }))),
      job: uniq(r.filter((x) => f.cust === "all" || x.customer_id === f.cust).map((x) => ({ id: x.job_id, name: `${x.customer_name} · ${x.job_name}` }))),
    };
  }, [rows, f.cust]);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v, ...(k === "cust" ? { job: "all" } : {}) }));
  const shown = (rows || []).filter((x) => (f.task === "all" || x.task === f.task) && (f.cust === "all" || x.customer_id === f.cust) && (f.job === "all" || x.job_id === f.job)
    && (f.emp === "all" || [...x.assigned, ...x.working_now, ...x.booked_by].some((p) => p.id === f.emp)));
  const people = (list) => list.map((p) => p.name).join(", ");
  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <PageHeader title="Live Jobs" subtitle="Every task on open jobs: who it's assigned to, who's on it now and the hours booked" />
      <div className="flex flex-wrap gap-2 mb-4" data-testid="live-filters">
        <Filter label="Employee" value={f.emp} onChange={set("emp")} opts={o.emp} testid="live-filter-employee" />
        <Filter label="Task" value={f.task} onChange={set("task")} opts={o.task} testid="live-filter-task" />
        <Filter label="Customer" value={f.cust} onChange={set("cust")} opts={o.cust} testid="live-filter-customer" />
        <Filter label="Job" value={f.job} onChange={set("job")} opts={o.job} testid="live-filter-job" />
        {Object.values(f).some((v) => v !== "all") && <Button variant="ghost" onClick={() => setF({ emp: "all", task: "all", cust: "all", job: "all" })} data-testid="live-filter-clear">Clear</Button>}
      </div>
      {err && <div className="mb-4 p-3 rounded border border-red-500/40 bg-red-500/10 text-sm text-red-400" data-testid="live-jobs-error">Couldn't load live jobs: {err}</div>}
      {!rows ? <div className="text-muted-foreground">Loading…</div> : err && !rows.length ? null : shown.length === 0 ? <Empty text={rows.length ? "No live tasks match these filters." : "No open jobs have tasks yet. Add tasks to a job and they'll show here."} /> : (
        <div className="card-industrial overflow-x-auto">
          <table className="w-full text-sm" data-testid="live-jobs-table">
            <thead className="bg-accent/40 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
              <tr><th className="text-left px-4 py-3">Customer / Job</th><th className="text-left px-4 py-3">Task</th><th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Assigned</th><th className="text-left px-4 py-3">On it now</th><th className="text-right px-4 py-3">Hours booked</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {shown.map((x) => (
                <tr key={x.stage_id} data-testid={`live-row-${x.stage_id}`} className="hover:bg-accent/20 transition-colors">
                  <td className="px-4 py-3 max-w-[260px]"><JobTitle j={x} /></td>
                  <td className="px-4 py-3 font-semibold">{x.task}</td>
                  <td className="px-4 py-3 text-xs font-mono uppercase text-muted-foreground">{x.working_now.some((p) => p.status === "running") && x.status === "awaiting_work" ? <span className="text-emerald-400">Working</span> : STATUS[x.status] || x.status}</td>
                  <td className="px-4 py-3 text-muted-foreground">{people(x.assigned) || "Unassigned"}</td>
                  <td className="px-4 py-3" data-testid={`live-now-${x.stage_id}`}>{x.working_now.length ? x.working_now.map((p) => <div key={p.id} className={p.status === "running" ? "text-emerald-400" : "text-amber-400"}>● {p.name} <span className="text-xs text-muted-foreground">{hhmm(p.elapsed_seconds / 60)}{p.status !== "running" ? " (paused)" : ""}</span></div>) : <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-3 text-right font-mono-num" data-testid={`live-hours-${x.stage_id}`}>{hhmm(x.booked_minutes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
